/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {HttpClient, HttpErrorResponse, HttpEvent} from "@angular/common/http";
import {inject, Injectable, Injector} from "@angular/core";
import {catchError, map, Observable, of, tap} from "rxjs";
import {IResponseApi, isV2ApiResponse, RESPONSE_CODE, V2ApiResponseError} from "@core/models";
import {ERROR_CODE} from "@core/constants";
import {AppMessages} from "@shared/constants";
// CHÚ Ý: Luôn import trực tiếp từ file nguồn, KHÔNG import AuthService từ barrel "@core/authentication"
// để tránh lỗi Circular Dependency khiến Class extends value undefined.
import {AuthService} from "../authentication/auth.service";
import {environment} from "@environments/environment";
import {castInt, parseParams} from "@shared/utils";
// import { AuthService } from "../authentication"; // Phụ thuộc vòng - sử dụng injector bên dưới

type resTypeHttp = any & 'arraybuffer' | 'blob' | 'json' | 'text';

/**
 * Handler class
 * 1. Func get data common.
 * 2. Func format data.
 * 3. Func some thing
 * */
@Injectable({
  providedIn: 'any'
})
export class ApiService {
  httpClient = inject(HttpClient);
  injector = inject(Injector);
  authService = inject(AuthService);

  /**
   * ---------------------------------------------------------------------------------------------------------
   * Format data ------------------------------------------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------
   * */
  getParamsCommon(params: any = {}, isFormData = false) {
    let bizId = this.authService.businessCurrent?.id;

    // Hỗ trợ Admin: Nếu user là admin và chưa có businessCurrent.id nhưng URL có ?businessId=xxx
    if (!bizId && this.authService.isAdmin() && typeof window !== 'undefined') {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlBizId = urlParams.get('businessId');
        if (urlBizId && Number(urlBizId) > 0) {
          bizId = Number(urlBizId);
        }
      } catch {
        /* Bỏ qua nếu môi trường không hỗ trợ URLSearchParams */
      }
    }

    if (isFormData) {
      params.append('businessId', bizId);
      return params;
    }

    if (!params['businessId']) {
      params['businessId'] = bizId;
    }
    return params;
  }

  getBaseUrlApi(endpoint: string): string {
    return environment.baseUrlApi + endpoint;
  }


  getResponseDefault<T = IResponseApi>(messages: any[] = [AppMessages.INVALID_DATA]) {
    return of({
      code: 0,
      errorCode: null,
      statusCode: '',
      messages: messages,
      data: [],
    }) as Observable<T>;
  }

  /**
   * ---------------------------------------------------------------------------------------------------------
   * Request method api Nhanh ----------------------------------------------------------
   * ---------------------------------------------------------------------------------------------------------
   * */

  handleError(messages: any = null) {
    return catchError<any | Observable<IResponseApi>, any>((err: HttpErrorResponse) => {
        const body: IResponseApi<[]> = {
          code: 0,
          errorCode: null,
          statusCode: castInt(err.status),
          messages: [],
          data: [],
        }
        return of(body);
      }
    )
  }

  private handleTapRes(): any {
    return tap({
      next: (res: IResponseApi) => {
        if (res.errorCode == ERROR_CODE.ERR_PAGE_401) {
          // Something code handle
        }
      }
    });
  }

  /**
   * Chuẩn hóa response từ BE về format V2 thống nhất: { code, data, messages }
   *
   * Các dạng BE có thể trả:
   * - V2 (chuẩn): { code: 1, data: {...}, messages: [] }
   * - V1 (cũ): { msg: {...} }
   * - Array data: [...]
   * - Response không code: { items: [...], total: N, ... } — các format cũ khác
   *
   * Nguyên tắc: response không có field "code" được coi là thành công (code=1)
   * trừ khi provider trả payload lỗi tường minh qua `error` hoặc `err`.
   */
  private normalizeV1Response<T = IResponseApi>(response: T): T {
    // Server trả về null hoặc không phải object → trả về response lỗi chuẩn
    if (response === null || response === undefined || typeof response !== 'object') {
      console.warn('[ApiService] Response không hợp lệ (null/undefined/primitive), trả về default error', response);
      return {code: 0, messages: ['Response is null or invalid'], data: null} as unknown as T;
    }

    // Server trả về mảng → wrap vào data
    if (Array.isArray(response)) {
      return {code: 1, data: response, messages: []} as unknown as T;
    }

    const obj = response as any;

    // Một số endpoint proxy trực tiếp lỗi của provider (Facebook, ...), ví dụ:
    // { error: '...', err: { code, detailError } }. Payload này không có `code`
    // của VPage nên không được phép rơi vào fallback V1 và bị hiểu nhầm là success.
    if (this.isProviderErrorResponse(obj)) {
      return this.normalizeProviderErrorResponse(obj) as T;
    }

    // Đã là V2 format (có field code) → giữ nguyên
    if ('code' in obj) {
      return response;
    }

    // V1 format: { msg: {...} } → lấy data từ msg
    if ('msg' in obj) {
      return {code: 1, data: obj.msg, messages: []} as unknown as T;
    }

    // Các format cũ khác không có field code: coi là thành công,
    // bọc toàn bộ response vào data để component xử lý bình thường
    return {code: 1, data: obj, messages: []} as unknown as T;
  }

  /**
   * V2 APIs encode application errors in an HTTP-200 body. Consumers that need
   * an operation to stop on failure should use this shared guard instead of
   * comparing `code` themselves. Legacy responses remain untouched.
   */
  protected requireV2Success<T>(response: T): T {
    if (!isV2ApiResponse(response) || Number(response.code) === RESPONSE_CODE.SUCCESS) {
      return response;
    }
    throw new V2ApiResponseError(response);
  }

  private isProviderErrorResponse(response: Record<string, any>): boolean {
    return (Object.prototype.hasOwnProperty.call(response, 'error') && response['error'] != null)
      || (Object.prototype.hasOwnProperty.call(response, 'err') && response['err'] != null);
  }

  protected normalizeProviderErrorResponse(response: Record<string, any>): IResponseApi<null, string[]> {
    const providerError = response['err'] ?? response['error'];
    const detailError = providerError && typeof providerError === 'object'
      ? providerError.detailError ?? providerError
      : null;
    const message = detailError?.error_user_msg
      || detailError?.message
      || (typeof providerError === 'string' ? providerError : '')
      || response['error']
      || AppMessages.INVALID_DATA;
    const errorCode = detailError?.error_subcode
      ?? detailError?.code
      ?? providerError?.code
      ?? response['errorCode']
      ?? null;

    return {
      code: 0,
      errorCode: errorCode == null ? null : String(errorCode),
      statusCode: response['statusCode'],
      messages: [String(message)],
      data: null,
    };
  }


  postFormDataRequest<T = IResponseApi>(endpoint: string, formData: FormData): Observable<T | any> {
    formData = this.getParamsCommon(formData, true);

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), formData).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * Used in cases where it is necessary to monitor upload/download progress events.
   * @returns An Observable of HttpEvent<object> containing progress and response events.
   */
  postFormDataRequestProgress<T = IResponseApi>(endpoint: string, formData: FormData): Observable<HttpEvent<object>> {
    formData = this.getParamsCommon(formData, true);

    const options = {
      reportProgress: true,
      observe: "events" as const
    }
    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), formData, options).pipe(
      this.handleTapRes(),
      this.handleError()
    );
  }

  /**
   * Upload progress cho endpoint theo Page/User. Không tự chèn businessId hiện tại.
   */
  postFormDataUserRequestProgress<T = IResponseApi>(endpoint: string, formData: FormData): Observable<HttpEvent<object>> {
    const options = {
      reportProgress: true,
      observe: 'events' as const,
    };
    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), formData, options).pipe(
      this.handleTapRes(),
      this.handleError(),
    );
  }


  /**
   * Chú ý API hầu hết đều dùng POST nên cần xem tài liệu trước khi dùng
   * */
  getRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }
    params = this.getParamsCommon(params);
    const body = {
      params: params,
      responseType: resType
    }


    return this.httpClient.get<T>(this.getBaseUrlApi(endpoint), body).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  // test Zalo cá nhân local
  postRequestLocal<T = IResponseApi>(url: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }

    params = this.getParamsCommon(params);
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(url, params, options).pipe(
      this.handleTapRes(),
      map(res => this.normalizeV1Response(res)),
      this.handleError()
    )
  }

  postRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }

    params = this.getParamsCommon(params);
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      map(res => this.normalizeV1Response(res)),
      this.handleError()
    )
  }

  /** POST request for endpoints documented with the versioned V2 response contract. */
  protected postV2Request<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    return this.postRequest<T>(endpoint, params, resType).pipe(
      map(response => this.requireV2Success(response)),
    );
  }

  postJsonRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    if (!this.authService.businessCurrent?.id) {
      return this.getResponseDefault()
    }

    const body = this.getParamsCommon({...params});
    const options = {
      headers: {'Content-Type': 'application/json'},
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), body, options).pipe(
      this.handleTapRes(),
      map(res => this.normalizeV1Response(res)),
      this.handleError()
    )
  }

  /**
   * Chú ý API hầu hết đều dùng GET nên cần xem tài liệu trước khi dùng
   * khác ở chỗ không có businessId
   * */
  getUserRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = this.getParamsCommon(params);
    const body = {
      params: params,
      responseType: resType
    }


    return this.httpClient.get<T>(this.getBaseUrlApi(endpoint), body).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * khác postRequest ở chỗ không có businessId
   * @param endpoint
   * @param params
   * @param resType
   * @param headers header riêng của request (vd. Authorization của phiên đang đăng nhập Nhanh ở /auth/callback)
   * @returns
   */
  postUserRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json', headers?: Record<string, string>): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType,
      ...(headers ? {headers} : {})
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  postUserJsonRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    const options = {
      headers: {'Content-Type': 'application/json'},
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  postSystemRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * Dùng cho các API POST dành cho GUEST có thể xem được
   * */
  postGuestRequest<T = IResponseApi>(endpoint: string, params = {}, resType: resTypeHttp = 'json'): Observable<T> {
    params = parseParams(params);
    const options = {
      responseType: resType
    }

    return this.httpClient.post<T>(this.getBaseUrlApi(endpoint), params, options).pipe(
      this.handleTapRes(),
      this.handleError()
    )
  }

  /**
   * Hàm sẽ nối thêm businessId để tiện theo dõi request của DN nào tiện lợi trong quá trình debug API khi cần
   * */
  getNewBaseUrlApi(endpoint: string, params: any): string {
    let url = environment.baseUrlApi + endpoint;

    // Lấy businessId từ params nếu có, nếu không có thì lấy từ authService
    const providedBusinessId = params && typeof params === 'object'
      ? params.businessId
      : undefined;
    const businessId = providedBusinessId ?? this.authService.businessCurrent?.id;

    // Kiểm tra điều kiện nối businessId vào URL
    if (
      businessId !== undefined &&
      businessId !== null &&
      !url.includes('businessId=')
    ) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}businessId=${encodeURIComponent(businessId)}`;
    }

    return url;
  }
}
